<script setup lang="ts">
import { computed, PropType } from "vue";
import { ActivePin } from "@/modules/explorer/explorerTypes";
import { useExplorerStore } from "@/modules/explorer/store";
import { useFilterDialog } from "@/modules/explorer/components/FilterDialog/useFilterDialog";
import { useI18n } from "vue-i18n";
import { useDashboardStore } from "@/modules/dashboards/store";
import { useApiClient } from "@/api/client";
import useDialog from "@/ui/composables/useDialog";
import { filterSupportsQuickFilter } from "@/modules/explorer/components/FilterBar/filterUtils";
const { openFilterDialog } = useFilterDialog();
const { t } = useI18n();

const dialog = useDialog();

const props = defineProps({
	surface: {
		type: String as PropType<"explorer" | "dashboard">,
		required: true,
	},
	activePin: {
		type: Object as PropType<ActivePin>,
		require: true,
	},
});

const unpinDimension = async () => {
	const choice = await dialog.confirm({
		title: "Unpin chip",
		message: "Are you sure you want to unpin this chip?",
	});
	if (!choice) return;

	const result = await useApiClient("users").unpinFavoriteDimension(
		props.activePin.dimension
	);
	if (result.isOk()) {
		useExplorerStore().REMOVE_PIN({
			dimension: props.activePin.dimension,
			isApplied: true,
			valuesAreIncluded: true,
			source: props.activePin.source,
		});
	}
};
const editPin = () => {
	if (props.surface === "explorer") {
		useExplorerStore().explorerState.selectedFilter = props.activePin;
	} else {
		useDashboardStore().dashboardState.selectedFilter = props.activePin;
	}

	openFilterDialog();
};

const dimensionReference = computed(() => {
	const title = t(
		`properties.${props.activePin.dimension.name_clean}.title`
	)?.trim();

	const words = title
		.split(/[\s_-]+/)
		.map((word) => word.trim())
		.filter((word) => /^[A-Za-zÀ-ÖØ-öø-ÿ0-9]/.test(word));

	const getUppercaseLetters = (word: string) => {
		const matches = word.match(/[A-Z]/g);
		return matches ? matches.join("") : "";
	};

	if (words.length >= 2) {
		return words
			.slice(0, 3)
			.map((word) => {
				const upper = getUppercaseLetters(word);
				return upper ? upper[0] : word[0];
			})
			.join("");
	}

	const word = words[0] ?? "";
	const upper = getUppercaseLetters(word);

	if (upper.length >= 2) {
		return upper.slice(0, 3);
	}

	return word.slice(0, 3);
});

const toggleQuickFilterMenu = () => {
	useExplorerStore().TOGGLE_QUICK_FILTER_MENU_FROM_PIN(props.activePin);
};

const canUseQuickFilter = computed(() =>
	filterSupportsQuickFilter(props.activePin)
);
</script>

<template>
	<v-hover :close-delay="5000" v-slot="{ isHovering, props }">
		<v-chip
			v-bind="props"
			label
			color="inactive"
			variant="tonal"
			class="py-2"
			@click="editPin"
			style="
				border-radius: 3px !important;
				cursor: pointer;
				flex-shrink: 0;
				max-width: none;
			"
			size="small"
		>
			<div class="w-100 h-100 d-flex">
				<v-expand-x-transition>
					<div class="h-100" v-if="isHovering">
						<strong class="text-caption">
							{{ t(`properties.${activePin.dimension.name_clean}.title`) }}
						</strong>
					</div>
					<div v-else>
						<strong class="text-caption"> {{ dimensionReference }} </strong>
					</div>
				</v-expand-x-transition>
			</div>

			<v-btn
				v-if="isHovering && canUseQuickFilter"
				@click.stop="toggleQuickFilterMenu"
				rounded="0"
				class="ml-2"
				size="x-small"
				variant="tonal"
				:color="activePin.valuesAreIncluded ? 'inactive' : 'error'"
			>
				<i class="fa-light fa-list-tree fa-xl"></i>
			</v-btn>
			<v-btn
				v-if="isHovering && activePin.source !== 'role'"
				@click.stop="unpinDimension"
				rounded="0"
				class="ml-2"
				size="x-small"
				variant="tonal"
				:color="activePin.valuesAreIncluded ? 'inactive' : 'error'"
			>
				<i class="fa-light fa-thumbtack fa-xl"></i>
			</v-btn>
		</v-chip>
	</v-hover>
</template>

<style scoped></style>
